import React, { ReactNode } from 'react';
import { Box, Button, ButtonProps, IconButton, InputBase, ToggleButtonGroup, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add, Clear, SearchOutlined } from '@mui/icons-material';

export const PAGE_HEADER_HEIGHT = 72;
const HAIRLINE = '#EDF0F3';

type PageHeaderProps = {
    title: string;
    subtitle?: ReactNode;
    /** Rendered right after the title (search field, filters…) — takes the remaining width. */
    children?: ReactNode;
    /** Rendered on the far right (primary actions, toggles…). */
    actions?: ReactNode;
};

/**
 * Sticky page header shared by every dashboard page.
 * Height is fixed (PAGE_HEADER_HEIGHT) so pages can size their scroll areas with `calc(100vh - 72px)`.
 */
export const PageHeader = ({ title, subtitle, children, actions }: PageHeaderProps) => (
    <Box
        component='header'
        sx={{
            position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar,
            display: 'flex', alignItems: 'center', gap: 2,
            height: PAGE_HEADER_HEIGHT, px: 3,
            bgcolor: 'background.paper', borderBottom: `1px solid ${HAIRLINE}`,
        }}
    >
        <Box sx={{ flexShrink: 0, minWidth: 0 }}>
            <Typography component='h1' noWrap sx={{ fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2, color: 'text.primary' }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography noWrap sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
        {children && <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>{children}</Box>}
        {actions && <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>{actions}</Box>}
    </Box>
);

type SearchFieldProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Extra trailing controls (e.g. a filters trigger). */
    trailing?: ReactNode;
    maxWidth?: number | string;
};

export const SearchField = ({ value, onChange, placeholder = 'Rechercher', trailing, maxWidth = 520 }: SearchFieldProps) => (
    <Box sx={{
        flex: 1, maxWidth, minWidth: 0,
        display: 'flex', alignItems: 'center', gap: 0.5,
        height: 40, pl: 1.5, pr: 0.5,
        borderRadius: '10px', bgcolor: 'background.default', border: `1px solid ${HAIRLINE}`,
        transition: 'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
        '&:hover': { borderColor: '#D7DCE3' },
        '&:focus-within': { bgcolor: 'background.paper', borderColor: 'secondary.main', boxShadow: '0 0 0 3px rgba(46,107,235,0.12)' },
    }}>
        <SearchOutlined sx={{ fontSize: '1.25rem', color: 'text.secondary', flexShrink: 0 }} />
        <InputBase
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', '& input::placeholder': { color: 'text.secondary', opacity: 1 } }}
        />
        <IconButton
            size='small'
            aria-label='Effacer la recherche'
            onClick={() => onChange('')}
            sx={{ visibility: value ? 'visible' : 'hidden', color: 'text.secondary' }}
        >
            <Clear sx={{ fontSize: '1rem' }} />
        </IconButton>
        {trailing}
    </Box>
);

/** Rounded 40px icon button used for secondary header controls (filters…). */
export const HeaderIconButton = styled(IconButton)(({ theme }) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    '&:hover': { backgroundColor: '#E9ECF1', color: theme.palette.text.primary },
}));

/** Primary header action — blue, compact, with a leading "+" by default. */
export const HeaderPrimaryButton = ({ children, startIcon = <Add />, sx, ...props }: ButtonProps & { to?: string }) => (
    <Button
        variant='contained'
        color='secondary'
        disableElevation
        startIcon={startIcon}
        sx={{
            height: 40, px: 2, borderRadius: '10px',
            fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: '0 6px 16px rgba(46,107,235,0.22)',
            '&:hover': { boxShadow: '0 8px 20px rgba(46,107,235,0.30)' },
            ...sx,
        }}
        {...props}
    >
        {children}
    </Button>
);

/** Segmented control look for ToggleButtonGroup (e.g. "Intérimaires | Archivés"). */
export const SegmentedToggle = styled(ToggleButtonGroup)(({ theme }) => ({
    gap: 2,
    padding: 3,
    height: 40,
    borderRadius: 10,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${HAIRLINE}`,

    '& .MuiToggleButtonGroup-grouped': {
        border: 0,
        borderRadius: '8px !important',
        margin: 0,
        padding: '0 14px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        textTransform: 'none',
        color: theme.palette.text.secondary,
        transition: 'background-color 150ms ease, color 150ms ease, box-shadow 150ms ease',

        '&:hover': { backgroundColor: 'transparent', color: theme.palette.text.primary },
        '&.Mui-selected': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontWeight: 600,
            boxShadow: '0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.04)',
            '&:hover': { backgroundColor: theme.palette.background.paper },
        },
    },
}));
